import _ from 'lodash';

import { CoreEnums } from '@/utils/domain/constants';
import { sortFn } from '@/utils/infrastructure';
import { fieldConfigurationMapping } from '../field-configuration';
import { behaviorCasesByField, getFieldInitialBehavior } from './fieldBehaviors';
import { mergeUniqueRules } from './utils';

const { FormFieldBehavior } = CoreEnums;

const DEFAULT_COLUMN_WIDTH = 150;
const CHUNK_WIDTH_MULTIPLIER = 36;
const CHUNK_SIZE = 4;

export const createTableFieldConfig = ({
  field,
  fieldConfig,
  initialBehavior,
  behaviorCases,
  tableInternalName,
}) => ({
  id: field?.id,
  name: field?.internalName,
  label: field?.displayText,
  internalTableField: true,
  ...fieldConfig,
  ...initialBehavior,
  ...(!initialBehavior?.isLocked &&
    field?.shouldUpdate && {
      shouldUpdate: true,
      behaviorCases: behaviorCasesByField(field, behaviorCases),
      tableInternalName,
    }),
  rules: mergeUniqueRules(fieldConfig.rules, initialBehavior.rules),
});

export const shouldColumnBeVisible = (fieldConfig, field, initialBehavior) => {
  const visible = fieldConfig.isDetails ? field?.visibleInDetails : true;
  return !initialBehavior.hidden && visible;
};

export const createTableColumn = (field) => {
  const chunk = _.chunk(field?.displayText, CHUNK_SIZE);
  return {
    key: field?.internalName,
    dataIndex: field?.internalName,
    width: chunk.length > 3 ? chunk.length * CHUNK_WIDTH_MULTIPLIER : DEFAULT_COLUMN_WIDTH,
    title: field?.displayText,
    sorter: true,
    type: field?.type,
  };
};

export function mapTableFields(field, setup, operationType) {
  let records = [];
  const config = [];
  const columns = [];

  const {
    form,
    behaviorCases,
    data: instanceData,
    commandsBehaviorCases,
    versionId,
    processId,
  } = setup;

  const { fieldBehavior } = _.find(behaviorCases, (be) => be.field?.id === field?.id) ?? {};

  const fields = form.fields
    .filter((fld) => fld.parentTable?.id === field?.id && fld?.enabled)
    .sort(sortFn)
    .map((fld) => {
      const { fieldIterator } = require('./fieldMappers');
      return fieldIterator(fld, setup);
    })
    .filter(Boolean);

  if (fields.length > 0) {
    const { data = [] } = instanceData?.[field?.internalName] ?? {};

    records = data
      .sort((a, b) => (a.lineNumber ?? 0) - (b.lineNumber ?? 0))
      .map(({ id, instanceTableLine }) => ({ id, ...instanceTableLine }));

    _.each(fields, (fieldItem) => {
      const { createFieldBase } = require('./fieldMappers');
      const fieldBase = createFieldBase(fieldItem, {
        operationType,
        formId: form?.id,
        processId,
        versionId,
        instanceData,
      });

      const initialBehavior = getFieldInitialBehavior(fieldItem);
      const fieldConfig = fieldConfigurationMapping(fieldBase, form.fields);
      const configItem = createTableFieldConfig({
        field: fieldItem,
        fieldConfig,
        initialBehavior,
        behaviorCases,
        tableInternalName: field?.internalName,
      });

      config.push(configItem);
      const shouldShowColumn = shouldColumnBeVisible(fieldConfig, fieldItem, initialBehavior);

      if (shouldShowColumn) {
        columns.push(createTableColumn(fieldItem));
      }
    });
  }

  if (!fields.length) return null;

  const base = {
    ...field,
    ...instanceData?.[field.internalName],
    versionId,
    processId,
    formId: form?.id,
  };

  const isLocked = field.fieldBehavior === CoreEnums.FormFieldBehavior.Locked;
  const commandBehaviorCases = _.filter(commandsBehaviorCases, (be) => be?.field?.id === field?.id);
  const fieldConfig = fieldConfigurationMapping(base, form.fields);

  return {
    columnWidth: field?.columnWidth,
    ...fieldConfig,
    ...getFieldInitialBehavior(field),
    ...(commandBehaviorCases?.length > 0 && { commandBehaviorCases }),
    ...(!isLocked &&
      field?.shouldUpdate && {
        shouldUpdate: true,
        behaviorCases: behaviorCasesByField(field, behaviorCases),
      }),

    readOnly: fieldBehavior === FormFieldBehavior.ReadOnly,
    hidden: !fields.length || fieldBehavior === FormFieldBehavior.Hidden,
    name: field?.internalName,
    id: field?.id,
    // UI component configuration
    columns,
    records,
    config,
    fields,
  };
}
